import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { CardContent, Typography, Box, Divider, useTheme, useMediaQuery, CardActionArea } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { GlassCard } from '../../../components/ui';
import ItemIcon from '../../../components/ItemIcon';
import type { WikiSkillItem } from '../../../types';
import { WIKI_CARD } from '../../../utils/responsive';
import { ACCENT_COLORS, BG_COLORS, TEXT_COLORS } from '../../../utils/colors';

interface WikiSkillCardProps {
  skill: WikiSkillItem;
  source: string;
  raceId?: number;
  skillTypeId?: number;
  classId?: number;
  professionId?: number;
  onPreview?: (itemId: number, detailUrl: string) => void;
  /** Карточка открыта в боковой панели (десктоп) — подсвечиваем, как выбранную строку в поиске вики. */
  selected?: boolean;
}


const WikiSkillCard: React.FC<WikiSkillCardProps> = ({
  skill,
  source,
  raceId,
  skillTypeId,
  classId,
  professionId,
  onPreview,
  selected = false
}) => {
  const { t } = useTranslation('wiki');
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.up('sm'));
  const isMd = useMediaQuery(theme.breakpoints.up('md'));
  const iconSize = isMd ? WIKI_CARD.iconSize.md : isSm ? WIKI_CARD.iconSize.sm : WIKI_CARD.iconSize.xs;

  // Build RESTful URL for skill detail page
  const getSkillPath = () => {
    let path = `/wiki/${source}`;
    if (raceId) {
      path += `/races/${raceId}`;
      if (skillTypeId) {
        path += `/types/${skillTypeId}`;
        if (classId) {
          path += `/classes/${classId}`;
          if (professionId) {
            path += `/professions/${professionId}`;
          }
        }
      }
    }
    path += `/details/${skill.skill_id}`;
    return path;
  };

  return (
    <GlassCard
      sx={{
        height: '100%',
        ...(selected && { borderColor: ACCENT_COLORS.PRIMARY, background: BG_COLORS.GLASS_LIGHTER }),
        '&:hover': {
          transform: 'translateY(-4px) translateZ(0)',
          borderColor: selected ? ACCENT_COLORS.PRIMARY : 'rgba(185, 28, 28, 0.6)',
          background: BG_COLORS.GLASS_LIGHTER,
          boxShadow: 'none',
        },
      }}
    >
      <CardActionArea
        {...(onPreview
          ? { onClick: () => onPreview(skill.skill_id, getSkillPath()) }
          : { component: RouterLink, to: getSkillPath() }
        )}
        sx={{
          height: '100%',
          '& .MuiCardActionArea-focusHighlight': {
            background: 'transparent',
          },
        }}
      >
        <CardContent sx={{ textAlign: 'center', p: WIKI_CARD.padding }}>
        {/* Skill Icon */}
        <Box sx={{ mb: WIKI_CARD.iconMargin, display: 'flex', justifyContent: 'center' }}>
          {skill.icon_skill && skill.icon_skill !== '-' && (
            <ItemIcon
              icon={skill.icon_skill}
              iconPanel={skill.icon_panel !== '-' ? skill.icon_panel : ''}
              name={skill.name}
              size={iconSize}
            />
          )}
        </Box>

        {/* Skill Name */}
        <Typography
          variant="subtitle1"
          component="h3"
          sx={{
            fontWeight: 600,
            mb: { xs: 0.5, sm: 1 },
            fontSize: WIKI_CARD.titleFontSize,
            lineHeight: 1.3,
            minHeight: WIKI_CARD.titleMinHeight,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden'
          }}
        >
          {skill.name}
        </Typography>

        {/* Skill Details */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: WIKI_CARD.gap, mt: { xs: 0.5, sm: 1 } }}>
          <Divider sx={{ my: WIKI_CARD.gap, opacity: 0.6 }} />

          {/* Skill Stats Grid */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 0.15, sm: 0.25 } }}>
            {/* Level Detail */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: WIKI_CARD.detailsFontSize }}>
                {t('skillProperties.level')}:
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: WIKI_CARD.detailsFontSize,
                  fontWeight: 600,
                  color: TEXT_COLORS.PRIMARY
                }}
              >
                {skill.level}
              </Typography>
            </Box>

            {/* Type Detail */}
            {skill.operator_type && skill.operator_type !== 'NONE' && skill.operator_type !== '-' && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: WIKI_CARD.detailsFontSize }}>
                  {t('skillProperties.type')}:
                </Typography>
                <Typography
                  variant="caption"
                  sx={{
                    fontSize: WIKI_CARD.detailsFontSize,
                    fontWeight: 600,
                    color: TEXT_COLORS.PRIMARY
                  }}
                >
                  {skill.operator_type}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </CardContent>
      </CardActionArea>
    </GlassCard>
  );
};

export default WikiSkillCard;